<script lang="ts">
    import {onMount, createEventDispatcher} from 'svelte';
    import {timeSeriesApi} from '../lib/api';
    import {formatDate} from '../lib/utils';
    import type {TimeSeriesMeta} from '../lib/types';
    import {generateDummyUserId} from '../stores/timeseriesStore';

    const dispatch = createEventDispatcher();

    // State
    let timeseries: TimeSeriesMeta[] = [];
    let loading = true;
    let error = '';
    let showCreateForm = false;
    let createFormData = {
        name: '',
        description: '',
        frequency: 'daily',
        tags: ''
    };
    let searchTerm = '';

    // Pagination
    let skip = 0;
    const limit = 10;
    let total = 0;

    // Load time series on mount
    onMount(async () => {
        await loadTimeSeries();
    });

    // Filtered time series based on search term
    $: filteredTimeSeries = searchTerm
        ? timeseries.filter(ts =>
            ts.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (ts.description && ts.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (ts.tags && ts.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())))
        )
        : timeseries;

    // Load time series data from API
    async function loadTimeSeries() {
        try {
            loading = true;
            error = '';

            const response = await timeSeriesApi.getAll(skip, limit);
            timeseries = response.data;
            total = response.pagination.total;

            loading = false;
        } catch (err) {
            error = err instanceof Error ? err.message : 'Failed to load time series';
            loading = false;
        }
    }

    // Navigate to time series detail page
    function goToTimeSeriesDetail(id: string) {
        dispatch('navigate', {page: 'timeseries', params: {id}});
    }

    // Create a new time series
    async function createTimeSeries() {
        try {
            loading = true;
            error = '';

            // Process tags if provided
            const tagsArray = createFormData.tags
                ? createFormData.tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0)
                : [];

            // Generate a UUID for testing purposes (in production, this would come from auth)
            const userId = generateDummyUserId();

            const newTimeSeries = await timeSeriesApi.create({
                name: createFormData.name,
                description: createFormData.description,
                frequency: createFormData.frequency,
                tags: tagsArray,
                user_id: userId
            });

            // Reset form and refresh list
            createFormData = {
                name: '',
                description: '',
                frequency: 'daily',
                tags: ''
            };
            showCreateForm = false;

            // Go to the new time series detail page
            goToTimeSeriesDetail(newTimeSeries.id);
        } catch (err) {
            error = err instanceof Error ? err.message : 'Failed to create time series';
            loading = false;
        }
    }

    // Delete a time series
    async function deleteTimeSeries(event: Event, id: string) {
        event.stopPropagation();

        if (!confirm('Вы уверены, что хотите удалить этот временной ряд? Это действие нельзя отменить.')) {
            return;
        }

        try {
            loading = true;
            error = '';

            await timeSeriesApi.delete(id);
            await loadTimeSeries();

            loading = false;
        } catch (err) {
            error = err instanceof Error ? err.message : 'Failed to delete time series';
            loading = false;
        }
    }

    // Handle pagination
    function nextPage() {
        if (skip + limit < total) {
            skip += limit;
            loadTimeSeries();
        }
    }

    function prevPage() {
        if (skip > 0) {
            skip = Math.max(0, skip - limit);
            loadTimeSeries();
        }
    }
</script>

<div class="dashboard">
    <div class="d-flex justify-content-between align-items-center mb-4">
        <h2 class="mb-0">Панель управления временными рядами</h2>

        <button
                class="btn btn-primary"
                on:click={() => showCreateForm = !showCreateForm}
        >
            {#if showCreateForm}
                <i class="bi bi-x-circle me-2"></i>
                Отмена
            {:else}
                <i class="bi bi-plus-circle me-2"></i>
                Новый временной ряд
            {/if}
        </button>
    </div>

    {#if error}
        <div class="alert alert-danger alert-dismissible fade show" role="alert">
            {error}
            <button type="button" class="btn-close" on:click={() => error = ''} aria-label="Close"></button>
        </div>
    {/if}

    {#if showCreateForm}
        <div class="card mb-4">
            <div class="card-header">
                <h5 class="mb-0">Создать новый временной ряд</h5>
            </div>
            <div class="card-body">
                <form on:submit|preventDefault={createTimeSeries}>
                    <div class="mb-3">
                        <label for="name" class="form-label">Название *</label>
                        <input
                                type="text"
                                class="form-control"
                                id="name"
                                bind:value={createFormData.name}
                                required
                        />
                    </div>

                    <div class="mb-3">
                        <label for="description" class="form-label">Описание</label>
                        <textarea
                                class="form-control"
                                id="description"
                                rows="3"
                                bind:value={createFormData.description}
                        ></textarea>
                    </div>

                    <div class="mb-3">
                        <label for="frequency" class="form-label">Частота *</label>
                        <select
                                class="form-select"
                                id="frequency"
                                bind:value={createFormData.frequency}
                                required
                        >
                            <option value="hourly">Ежечасно</option>
                            <option value="daily">Ежедневно</option>
                            <option value="weekly">Еженедельно</option>
                            <option value="monthly">Ежемесячно</option>
                            <option value="quarterly">Ежеквартально</option>
                            <option value="yearly">Ежегодно</option>
                        </select>
                    </div>

                    <div class="mb-3">
                        <label for="tags" class="form-label">Теги</label>
                        <input
                                type="text"
                                class="form-control"
                                id="tags"
                                bind:value={createFormData.tags}
                                placeholder="Теги через запятую (например, продажи, прогноз, финансы)"
                        />
                    </div>

                    <div class="d-flex justify-content-end">
                        <button
                                type="button"
                                class="btn btn-outline-secondary me-2"
                                on:click={() => showCreateForm = false}
                        >
                            Отмена
                        </button>
                        <button
                                type="submit"
                                class="btn btn-primary"
                                disabled={loading || !createFormData.name || !createFormData.frequency}
                        >
                            {#if loading}
                                <span class="spinner-border spinner-border-sm me-2" role="status"
                                      aria-hidden="true"></span>
                                Создание...
                            {:else}
                                Создать временной ряд
                            {/if}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    {/if}

    <div class="card mb-4">
        <div class="card-header">
            <div class="d-flex justify-content-between align-items-center">
                <h5 class="mb-0">Временные ряды</h5>
                <div class="input-group" style="max-width: 300px;">
          <span class="input-group-text">
            <i class="bi bi-search"></i>
          </span>
                    <input type="text" class="form-control" placeholder="Поиск..." bind:value={searchTerm}/>
                    {#if searchTerm}
                        <button
                                class="btn btn-outline-secondary"
                                type="button"
                                on:click={() => searchTerm = ''}
                        >
                            <i class="bi bi-x"></i>
                        </button>
                    {/if}
                </div>
            </div>
        </div>
        <div class="card-body">
            {#if loading && timeseries.length === 0}
                <div class="text-center py-5">
                    <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Загрузка...</span>
                    </div>
                    <p class="mt-3">Загрузка данных временных рядов...</p>
                </div>
            {:else if filteredTimeSeries.length === 0}
                <div class="text-center py-5">
                    <i class="bi bi-bar-chart-line" style="font-size: 3rem; color: #dee2e6;"></i>
                    <p class="mt-3">
                        {searchTerm ? 'Нет временных рядов, соответствующих критериям поиска.' : 'Нет доступных временных рядов. Создайте один, чтобы начать!'}
                    </p>
                    {#if searchTerm}
                        <button class="btn btn-outline-primary" on:click={() => searchTerm = ''}>
                            Очистить поиск
                        </button>
                    {:else}
                        <button class="btn btn-primary" on:click={() => showCreateForm = true}>
                            <i class="bi bi-plus-circle me-2"></i>
                            Создать временной ряд
                        </button>
                    {/if}
                </div>
            {:else}
                <div class="row">
                    {#each filteredTimeSeries as ts (ts.id)}
                        <div class="col-md-6 col-lg-4 mb-4">
                            <div class="card h-100 time-series-card"
                                 role="button"
                                 tabindex="0"
                                 on:click={() => goToTimeSeriesDetail(ts.id)}
                                 on:keydown={e => e.key === 'Enter' && goToTimeSeriesDetail(ts.id)}>
                                <div class="card-body">
                                    <div class="d-flex justify-content-between">
                                        <h5 class="card-title">{ts.name}</h5>
                                        <button
                                                class="btn btn-sm btn-outline-danger"
                                                on:click={(e) => deleteTimeSeries(e, ts.id)}
                                                title="Удалить временной ряд"
                                        >
                                            <i class="bi bi-trash"></i>
                                        </button>
                                    </div>
                                    <h6 class="card-subtitle mb-2 text-muted">
                                        <span class="badge bg-secondary me-2 text-capitalize">{ts.frequency}</span>
                                        <span class="text-muted">{formatDate(ts.created_at)}</span>
                                    </h6>
                                    <p class="card-text">
                                        {ts.description || 'Описание отсутствует.'}
                                    </p>
                                    {#if ts.tags && ts.tags.length > 0}
                                        <div class="tags-container mt-auto">
                                            {#each ts.tags as tag}
                                                <span class="badge bg-light text-dark me-1 mb-1">{tag}</span>
                                            {/each}
                                        </div>
                                    {/if}
                                </div>
                                <div class="card-footer text-end">
                                    <button class="btn btn-sm btn-primary">Просмотр деталей</button>
                                </div>
                            </div>
                        </div>
                    {/each}
                </div>

                {#if total > limit}
                    <div class="d-flex justify-content-between mt-4">
                        <p class="text-muted mb-0">
                            Showing {skip + 1} to {Math.min(skip + limit, total)} of {total} time series
                        </p>
                        <div class="btn-group">
                            <button
                                    class="btn btn-outline-secondary"
                                    on:click={prevPage}
                                    disabled={skip === 0}
                            >
                                <i class="bi bi-chevron-left"></i> Previous
                            </button>
                            <button
                                    class="btn btn-outline-secondary"
                                    on:click={nextPage}
                                    disabled={skip + limit >= total}
                            >
                                Next <i class="bi bi-chevron-right"></i>
                            </button>
                        </div>
                    </div>
                {/if}
            {/if}
        </div>
    </div>
</div>

<style>
    .time-series-card {
        cursor: pointer;
        transition: transform 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
        display: flex;
        flex-direction: column;
    }

    .time-series-card:hover {
        transform: translateY(-5px);
        box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
    }

    .time-series-card .card-body {
        flex: 1;
    }

    .tags-container {
        display: flex;
        flex-wrap: wrap;
    }

    .card-footer {
        background-color: rgba(0, 0, 0, 0.03);
        border-top: 1px solid rgba(0, 0, 0, 0.125);
    }

    h2 {
        color: var(--dark-bg);
        font-weight: 600;
    }

    .badge.bg-secondary {
        font-weight: 500;
    }
</style>
